<template>
    <div :id="id" v-if="shouldShow">
        <form-label v-if="label" :text="fieldLabel" :hint="fieldHint" :for="id"></form-label>
        <div class="uk-form-controls">
            <div class="rr-slider" ref="elem">
                <div class="rr-slider__bar">
                    <div class="rr-slider__bar-dot" ref="min"
                         :style="{left: left+'px'}"
                         @mousedown="startDrag('left')"
                         @touchstart="startDrag('left')"
                    ></div>
                    <div class="rr-slider__bar-range" ref="range"
                         :style="{left: left+'px', right: right+'px'}"
                    ></div>
                    <div class="rr-slider__bar-dot" ref="max"
                         :style="{right: right+'px'}"
                         @mousedown="startDrag('right')"
                         @touchstart="startDrag('right')"
                    ></div>
                </div>
            </div>
        </div>
        <p class="uk-text-center uk-margin-remove">{{ actualValueLeft / scaleFactor }} - {{ actualValueRight / scaleFactor }}</p>
        <form-error v-for="(error, index) in errors" :key="index" :error="error"></form-error>
    </div>

</template>

<script>
  import FormField from "rubinred/forms/js/mixins/FormField";

  export default {
    mixins: [FormField],
    props: {
      min: {
        type: Number,
        required: true,
      },
      max: {
        type: Number,
        required: true,
      },
      step: {
        type: Number,
        default: 1000,
      },
      minGap: {
        type: Number,
        default: 1000,
      },
      scaleFactor: {
        type: Number,
        default: 1,
      },
      value: {
        type: Array,
        default: null,
      },
    },
    data() {
      return {
        fieldValue: this.value || [this.min, this.max],
        left: 0,
        right: 0,
        size: null,
        virtualSize: this.max - this.min,
        offsetLeft: null,
        currentDot: 'left',
      };
    },
    computed: {
      actualValueLeft() {
        if (!this.size) {
          return this.min;
        }
        if(this.left === 0) {
          return this.min;
        } else {
          return this.roundStep(this.round(this.min + (this.left * this.virtualSize) / this.size));
        }
      },
      actualValueRight() {
        if (!this.size) {
          return this.max;
        }
        if(this.right === 0) {
          return this.max;
        } else {
          return this.roundStep(this.round(this.max - ((this.right * this.virtualSize) / this.size)));
        }
      },
      shouldShow() {
        return this.max - this.min > this.step;
      }
    },
    mounted() {
      if (!this.shouldShow) {
        return;
      }
      this.calculateStaticSizes();
      this.setValue(this.value);
    },
    methods: {
      bindEvents() {
        document.addEventListener('mousemove', this.dragging);
        document.addEventListener('touchmove', this.dragging, {passive: false}); //TODO check if passive needs to be false
        document.addEventListener('mouseup', this.endDrag);
        document.addEventListener('mouseleave', this.endDrag);
        document.addEventListener('touchend', this.endDrag, {passive: false});
      },
      unbindEvents() {
        document.removeEventListener('mousemove', this.dragging);
        document.removeEventListener('touchmove', this.dragging);
        document.removeEventListener('mouseup', this.endDrag);
        document.removeEventListener('mouseleave', this.endDrag);
        document.removeEventListener('touchend', this.endDrag);
      },
      getStepLeft(value) {
        let actual = this.round((value * this.virtualSize) / this.size + this.min);
        if((this.actualValueRight - actual) >= this.minGap) {
          if(actual < this.step) {
            return 0;
          }
          return this.getOffset(actual, 'right');
        } else {
          return null;
        }
      },
      getStepRight(value) {
        let actual = this.round(this.max - ((value * this.virtualSize) / this.size));
        if((actual - this.actualValueLeft) >= this.minGap) {
          if(actual === this.max) {
            return 0;
          }
          return this.getOffset(actual, 'left');
        } else {
          return null;
        }
      },
      startDrag(dot) {
        let body = document.querySelector('body');
        body.classList.add("rr-noselect");
        this.currentDot = dot;
        this.bindEvents();
      },
      dragging(e) {
        this.calculateStaticSizes();
        this.setOffset(this.getMouseOffset(e.clientX));
      },
      endDrag() {
        let body = document.querySelector('body');
        body.classList.add("rr-noselect");
        this.unbindEvents();
        this.fieldValue = [
          this.actualValueLeft,
          this.actualValueRight,
        ];
        this.triggerChangeAndValidate();
      },
      calculateStaticSizes() {
        if (!this.size) {
          this.size = this.$refs.elem.offsetWidth - 20;
        }
        if (!this.offsetLeft) {
          this.offsetLeft = this.$refs.elem.getBoundingClientRect().left;
        }
      },
      getMouseOffset(clientX) {
        let offset = 0;
        if (this.currentDot === 'left') {
          offset = clientX - this.offsetLeft;
        } else {
          offset = this.size - (clientX - this.offsetLeft);
        }

        return offset >= 0 ? offset : 0;
      },
      setOffset(offset) {
        if (this.currentDot === 'left') {
          if (this.getStepLeft(offset) != null) {
            this.left = this.getStepLeft(offset);
          }
        } else {
          if (this.getStepRight(offset) != null) {
            this.right = this.getStepRight(offset);
          }
        }
      },
      setValue(values) {
        let left = this.min;
        let right = this.max;
        if (Array.isArray(values)) {
          left = values[0] || this.min;
          right = values[1] || this.max;
        }
        left = left > this.min ? left : this.min;
        right = right < this.max ? right : this.max;

        this.left = (left * this.size - this.min * this.size) / this.virtualSize;
        this.right = (this.size * this.max - right * this.size) / this.virtualSize;
      },
      roundStep(number) {
        if (number === this.min || number === this.max) {
          return number;
        }
        if (number % this.step >= this.step / 2) {
          return Math.ceil(number / this.step) * this.step;
        } else {
          return Math.floor(number / this.step) * this.step;
        }
      },
      round(value) {
        if (Number.isInteger(value)) {
          return value;
        }
        if (Number.isInteger(this.step)) {
          return Math.floor(value);
        }

        let decimals = Math.pow(10, (this.step + '').split('.')[1].length);
        return Math.round(value * decimals) / decimals;

      },
      getOffset(a, direction) {
        if (Number.isInteger(this.step)) {
          let rounded = this.roundStep(a);
          if (direction === 'right') {
            return this.round(((rounded - this.min) * this.size) / this.virtualSize);
          } else if (direction === 'left') {
            return this.round(-1 * (this.size * (rounded - this.max) / this.virtualSize));
          }
        }
      },
    },
  }
</script>

<style lang="less">
    @import (reference) "~less/app";

    @barHeight: 10px;
    @dotDiameter: 18px;
    @borderRadius: 5px;
    .rr-slider {
        &__bar {
            width: 100%;
            height: 10px;
            border: 1px solid @global-border;
            background-color: @global-background;
            border-radius: @borderRadius;
            position: relative;
            overflow: visible;

            &-dot {
                position: absolute;
                width: @dotDiameter;
                height: @dotDiameter;
                top: -(@dotDiameter - @barHeight) / 2;
                z-index: 2;

                border: 1px solid @global-border;
                border-radius: 50%;
                background-color: @global-primary-background;
                cursor: pointer;

                &:first-child {

                }

                &:last-child {
                    right: 0;
                }
            }

            &-range {
                position: absolute;
                height: 100%;
                border-radius: @borderRadius;
                background-color: @global-primary-background;
            }
        }
    }
    .rr-noselect {
        -webkit-touch-callout: none; /* iOS Safari */
        -webkit-user-select: none; /* Safari */
        -khtml-user-select: none; /* Konqueror HTML */
        -moz-user-select: none; /* Firefox */
        -ms-user-select: none; /* Internet Explorer/Edge */
        user-select: none; /* Non-prefixed version, currently supported by Chrome and Opera */
    }
</style>



// WEBPACK FOOTER //
// public/themes/frontend/uikit3/extensions/rubinred/forms/assets/js/components/FormRangeSlider.vue